import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import LiveEmptyState from "../components/LiveEmptyState";
import useLiveMode from "../hooks/useLiveMode";
import api from "../lib/api";
import { Building2, ChevronRight, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", niche: "", region: "India", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [tree, setTree] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const { isLive, isConnected } = useLiveMode();

  const reload = () => Promise.all([
    api.get("/clients"),
    api.get("/clients/mcc/tree"),
    api.get("/clients/accounts/all"),
  ]).then(([c, t, a]) => { setClients(c.data); setTree(t.data); setAccounts(a.data); });

  useEffect(() => { reload(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name || "", niche: c.niche || "", region: c.region || "India", notes: c.notes || "" });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client created");
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? Linked accounts and campaigns will be unlinked, not deleted.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Layout title="Clients & Accounts" subtitle={`${clients.length} clients · ${accounts.length} accounts`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-muted-foreground">Manage clients, then map Google Ads accounts to them in Admin → Connectors.</div>
        <button onClick={openCreate} data-testid="client-add-btn" className="h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90 inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Add client
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-sm p-5 mb-6 max-w-2xl" data-testid="client-form">
          <div className="font-heading text-lg mb-4 tracking-tight">{editing ? "Edit client" : "Create client"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Client name *" className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="client-name" />
            <input value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} placeholder="Niche / industry" className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="client-niche" />
            <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Region" className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="client-region" />
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" className="h-20 px-3 py-2 border border-border rounded-sm text-sm md:col-span-2" data-testid="client-notes" />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={save} data-testid="client-save" className="h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm">{editing ? "Save changes" : "Create client"}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="h-9 px-4 border border-border text-sm rounded-sm">Cancel</button>
          </div>
        </div>
      )}

      {isLive && clients.length === 0 && accounts.length === 0 ? (
        isConnected ? (
          <LiveEmptyState
            entity="clients"
            variant="module"
            title="No clients created yet"
            message="Use the Add client button to create your first client, then map Google Ads accounts to it from Admin → Connectors."
          />
        ) : (
          <LiveEmptyState entity="clients" />
        )
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="clients-grid">
        {clients.map((c) => (
          <div
            key={c.id}
            data-testid={`client-card-${c.id}`}
            className="bg-card border border-border rounded-sm p-5 hover:border-primary/50 hover:shadow-sm transition-all relative group"
          >
            <Link to={`/clients/${c.id}`} className="block">
              <Building2 className="w-5 h-5 text-primary mb-3" strokeWidth={1.5} />
              <div className="font-heading font-medium text-base mb-1 pr-12">{c.name}</div>
              <div className="text-xs text-muted-foreground mb-3">{c.niche || "—"}</div>
              <div className="flex items-center justify-between pt-3 border-t border-border text-xs">
                <div>
                  <div className="label-mono mb-0.5">Accounts</div>
                  <div className="font-semibold tabular">{c.account_count ?? 0}</div>
                </div>
                <div>
                  <div className="label-mono mb-0.5">Campaigns</div>
                  <div className="font-semibold tabular">{c.campaign_count ?? 0}</div>
                </div>
                <div>
                  <div className="label-mono mb-0.5">Region</div>
                  <div className="font-semibold tabular">{c.region || "—"}</div>
                </div>
              </div>
            </Link>
            <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={(e) => { e.preventDefault(); openEdit(c); }} data-testid={`client-edit-${c.id}`} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={(e) => { e.preventDefault(); remove(c); }} data-testid={`client-delete-${c.id}`} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-[hsl(var(--critical))]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <ChevronRight className="absolute bottom-4 right-4 w-4 h-4 text-muted-foreground opacity-30 group-hover:opacity-60" />
          </div>
        ))}
      </div>
      </>
      )}
    </Layout>
  );
}
